import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'

import type { HistoryActivity } from '../../../models/activityTypes'
import { DeviceType, ListStatus, TemplateTarget } from '../../../models/enums'
import preventEvents from '../../../utils/preventEvents'

import ConversationHeader from '../../Menu/Mobile/ConversationHeader'
import ScrollButton from '../ScrollButton'
import ConversationDisplay from './ConversationDisplay'
import TemplateGlobal from '../../Templates/TemplateGlobal'

interface ConversationPanelMobileProps {
  activities: Array<HistoryActivity>
  itemsInConversation: number
}

const ConversationPanelMobile = ({ activities, itemsInConversation }: ConversationPanelMobileProps): JSX.Element => {
  const agentData = useSelector((state: RootState) => state.retorikReducer.agentData)
  const currentDeviceType = useSelector((state: RootState) => state.viewReducer.currentDeviceType)
  const isWidget = useSelector((state: RootState) => state.viewReducer.isWidget)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const open = useSelector((state: RootState) => state.widgetReducer.open)

  const [opacityStyle, setOpacityStyle] = useState<string | undefined>('')
  const [scrollButtonDisplay, setScrollButtonDisplay] = useState<boolean>(false)
  const [scrollButtonDirection, setScrollButtonDirection] = useState<'top' | 'bottom'>('bottom')

  const retorikConversationRef = useRef<HTMLDivElement>(null)
  const botStartRef = useRef<HTMLDivElement>(null)
  const botEndRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const handleScroll = (): void => {
    if (retorikConversationRef.current) {
      const current = retorikConversationRef.current as HTMLDivElement

      if (current.scrollHeight > current.offsetHeight) {
        setScrollButtonDisplay(true)
        setScrollButtonDirection(current.scrollTop > -10 ? 'top' : 'bottom')
      }

      const percentage = Math.ceil((current.scrollHeight - current.clientHeight) / Math.abs(current.scrollTop))
      setOpacityStyle(`linear-gradient(to top, black ${100 - Math.min(percentage, 15)}%, transparent 100%)`)
    }
  }

  /**
   * Scroll to the botEndRef HTML element at the bottom of the view
   */
  const scrollHandler = (to: 'bottom' | 'top'): void => {
    switch (to) {
      case 'top':
        setScrollButtonDirection('bottom')
        botStartRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
        break
      case 'bottom':
        setScrollButtonDirection('top')
        botEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
        break
    }

    handleScroll()
  }

  useEffect(() => {
    timerRef.current && clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      scrollHandler('bottom')
    }, 100)

    return (): void => {
      timerRef.current && clearTimeout(timerRef.current)
    }
  }, [itemsInConversation, retorikConversationRef?.current?.scrollHeight])

  useLayoutEffect(() => {
    if (retorikConversationRef?.current) {
      isWidget && preventEvents(null, retorikConversationRef, undefined, true)
    }
  })

  useEffect(() => {
    if (listStatus === ListStatus.closed && botEndRef.current) {
      timerRef.current && clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        scrollHandler('bottom')
      }, 100)
    }
  }, [listStatus])

  return (
    <TemplateGlobal target={TemplateTarget.mobileConversation}>
      <ConversationHeader agentName={agentData?.name} imageUrl={agentData?.portrait} />
      <div
        id='retorik-conversation'
        ref={retorikConversationRef}
        className='rf-relative rf-h-full rf-max-h-full rf-overflow-y-scroll rf-overflow-x-hidden rf-scrollbar-thin rf-self-auto rf-mb-26 rf-justify-self-center rf-flex rf-flex-col-reverse rf-text-size-auto rf-text-trueblack rf-bg-truewhite rf-bg-opacity-80'
        style={{
          WebkitMaskImage: currentDeviceType === DeviceType.mobile ? opacityStyle : undefined
        }}
        onScroll={handleScroll}
        onWheel={handleScroll}
      >
        <div ref={botEndRef} />

        {/* Button to scroll to top / bottom of the conversation */}
        <ScrollButton
          direction={scrollButtonDirection}
          display={scrollButtonDisplay && open}
          scrollHandler={scrollHandler}
          className='rf-absolute rf-z-ui rf-bottom-8 rf-right-6 rf-w-9 rf-h-9 rf-cursor-pointer rf-bg-[#00000050] rf-p-[7px_12px] rf-rounded-[20px] rf-flex'
        />

        <div className='rf-h-max rf-w-full rf-py-4 rf-flex rf-flex-col rf-gap-3'>
          {/* All answers / responses */}
          {activities.map((activity: HistoryActivity, index: number) => {
            return (
              <ConversationDisplay
                key={index}
                userQuestion={activity.question}
                botResponses={activity.answers}
                isMobile={true}
                triggerScroll={() => scrollHandler('bottom')}
                canBeStreaming={index === activities.length - 1}
              />
            )
          })}
        </div>

        <div ref={botStartRef} />
      </div>
    </TemplateGlobal>
  )
}

export default ConversationPanelMobile
